import { useTranslation } from "react-i18next";
import ProductCustomizer from "../components/ProductCustomizer";
import "./OrderExtrasPage.css";

const INCLUDED_ITEM_KEYS = [
  "logoFit",
  "qrCode",
  "printReady",
  "proof",
  "revision",
  "printCoordination",
];

const HERO_TAG_KEYS = ["coasters", "fabricStand", "proof"];

const VIDEO_TAG_KEYS = ["quickSetup", "noTools", "easyToCarry"];

const signalBars = [
  "h-2 print-signal-delay-1",
  "h-4 print-signal-delay-2",
  "h-6 print-signal-delay-3",
  "h-3 print-signal-delay-4",
  "h-5 print-signal-delay-5",
  "h-7 print-signal-delay-6",
  "h-4 print-signal-delay-7",
  "h-6 print-signal-delay-8",
  "h-3 print-signal-delay-9",
  "h-5 print-signal-delay-10",
];

export default function OrderExtrasPage({ isLoggedIn, performer }) {
  const { t } = useTranslation("admin");

  const includedItems = INCLUDED_ITEM_KEYS.map((key) =>
    t(`orderExtrasPage.included.items.${key}`)
  );

  const heroTags = HERO_TAG_KEYS.map((key) =>
    t(`orderExtrasPage.hero.tags.${key}`)
  );

  const videoTags = VIDEO_TAG_KEYS.map((key) =>
    t(`orderExtrasPage.video.tags.${key}`)
  );

  return (
    <main
      className={[
        "print-products-page min-h-screen overflow-x-clip bg-black text-white",
        "transition-colors duration-300",
        "theme-light:bg-[#ead6c2] theme-light:text-[#241a16]",
      ].join(" ")}
    >
      {/* HERO */}
      <section
        className={[
          "relative overflow-hidden border-b border-white/10",
          "px-3 py-14 sm:px-4 md:py-20 lg:px-5 lg:py-24",
          "theme-light:border-[#5a3824]/20",
        ].join(" ")}
      >
        <div
          aria-hidden="true"
          className={[
            "pointer-events-none absolute inset-0",
            "bg-[radial-gradient(circle_at_82%_20%,rgba(239,68,68,0.22),transparent_36%)]",
            "theme-light:bg-[radial-gradient(circle_at_82%_20%,rgba(239,68,68,0.15),transparent_38%)]",
          ].join(" ")}
        />

        <div
          aria-hidden="true"
          className={[
            "paranoidi-hero-glow pointer-events-none",
            "absolute -right-24 top-8 h-96 w-96 rounded-full",
            "bg-red-500/10 blur-3xl",
          ].join(" ")}
        />

        <div
          aria-hidden="true"
          className={[
            "pointer-events-none absolute bottom-0 left-[26%]",
            "h-64 w-64 rounded-full bg-red-950/30 blur-3xl",
            "theme-light:bg-[#541717]/10",
          ].join(" ")}
        />

        <div
          aria-hidden="true"
          className={[
            "print-products-grid pointer-events-none absolute inset-0",
            "opacity-[0.045] theme-light:opacity-[0.025]",
          ].join(" ")}
        />

        <div
          className={[
            "relative mx-auto grid w-full max-w-7xl items-center",
            "gap-14 xl:grid-cols-[0.9fr_1.1fr] xl:gap-16",
          ].join(" ")}
        >
          <div className="print-products-fade-up max-w-3xl">
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-red-300 theme-light:text-[#b42318]">
                {t("orderExtrasPage.hero.eyebrow")}
              </p>

              <span
                className={[
                  "rounded-full border border-red-400/25 bg-red-500/10",
                  "px-3 py-1 text-[0.62rem] font-black uppercase",
                  "tracking-[0.16em] text-red-200",
                  "theme-light:border-red-500/30",
                  "theme-light:bg-red-50 theme-light:text-[#b42318]",
                ].join(" ")}
              >
                {t("orderExtrasPage.hero.badge")}
              </span>
            </div>

            <h1
              className={[
                "mt-5 max-w-4xl text-4xl font-black tracking-[-0.05em]",
                "sm:text-6xl lg:text-[4.25rem] lg:leading-[0.98] xl:text-7xl",
                "theme-light:text-[#241a16]",
              ].join(" ")}
            >
              {t("orderExtrasPage.hero.title")}
            </h1>

            <p
              className={[
                "mt-7 max-w-2xl text-lg font-medium leading-8 text-zinc-300",
                "theme-light:text-[#5a3824] md:text-xl",
              ].join(" ")}
            >
              {t("orderExtrasPage.hero.description")}
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href="#editori"
                className={[
                  "inline-flex items-center justify-center rounded-full",
                  "bg-red-500 px-7 py-4 text-sm font-black uppercase",
                  "tracking-[0.1em] text-white",
                  "transition duration-300",
                  "hover:-translate-y-0.5 hover:bg-red-400",
                  "hover:shadow-[0_14px_32px_rgba(239,68,68,0.25)]",
                ].join(" ")}
              >
                {t("orderExtrasPage.hero.openEditor")}
              </a>

              <a
                href="#tuotteet"
                className={[
                  "inline-flex items-center justify-center rounded-full",
                  "border border-white/15 bg-white/[0.03]",
                  "px-7 py-4 text-sm font-black uppercase",
                  "tracking-[0.1em] text-white",
                  "transition duration-300",
                  "hover:-translate-y-0.5 hover:border-white/30",
                  "hover:bg-white/[0.08]",
                  "theme-light:border-[#5a3824]/20",
                  "theme-light:bg-[#fff8ef] theme-light:text-[#541717]",
                  "theme-light:hover:border-red-500/30",
                  "theme-light:hover:bg-white",
                ].join(" ")}
              >
                {t("orderExtrasPage.hero.viewProducts")}
              </a>
            </div>

            <div
              className={[
                "mt-8 flex flex-wrap gap-3 text-xs font-black uppercase",
                "tracking-[0.1em] text-zinc-300",
                "theme-light:text-[#5a3824]",
              ].join(" ")}
            >
              {heroTags.map((item) => (
                <span
                  key={item}
                  className={[
                    "rounded-full border border-white/10 bg-white/[0.04]",
                    "px-4 py-2 transition duration-300",
                    "hover:border-red-400/30 hover:bg-red-500/[0.05]",
                    "theme-light:border-[#5a3824]/20",
                    "theme-light:bg-[#fff8ef]",
                    "theme-light:hover:border-red-500/30",
                  ].join(" ")}
                >
                  {item}
                </span>
              ))}
            </div>

            <div
              aria-hidden="true"
              className="mt-8 flex h-7 items-end gap-1.5"
            >
              {signalBars.map((barClass, index) => (
                <span
                  key={`${barClass}-${index}`}
                  className={[
                    "paranoidi-print-signal w-1.5 rounded-full",
                    "bg-red-400/75",
                    barClass,
                  ].join(" ")}
                />
              ))}
            </div>
          </div>

          {/* Tuotteet pidetään erillisissä korteissa, jotta molemmat näkyvät. */}
          <div
            className={[
              "print-products-fade-up print-products-delay-1",
              "relative mx-auto w-full max-w-3xl",
            ].join(" ")}
          >
            <div
              aria-hidden="true"
              className={[
                "pointer-events-none absolute left-[8%] top-[10%]",
                "h-[55%] w-[72%] rounded-full bg-red-500/10 blur-3xl",
              ].join(" ")}
            />

            <div
              className={[
                "relative grid gap-4",
                "sm:grid-cols-[minmax(0,1.15fr)_minmax(13rem,0.85fr)]",
                "sm:items-end lg:gap-6",
              ].join(" ")}
            >
              <div
                className={[
                  "paranoidi-hero-coasters print-products-image-card",
                  "group order-2 overflow-hidden rounded-[2rem]",
                  "border border-white/10 bg-zinc-950 p-3",
                  "shadow-2xl shadow-black/60",
                  "transition-colors duration-300",
                  "hover:border-red-400/30",
                  "sm:order-1 sm:p-4",
                  "theme-light:border-[#5a3824]/20",
                  "theme-light:bg-[#fff8ef]",
                  "theme-light:shadow-[0_20px_55px_rgba(58,43,24,0.14)]",
                ].join(" ")}
              >
                <img
                  src="/assets/print-products/paranoidi-matti-oksa-tuopinaluset.webp"
                  alt={t("orderExtrasPage.hero.coastersAlt")}
                  className={[
                    "h-auto w-full rounded-[1.4rem] object-contain",
                    "transition duration-500 group-hover:scale-[1.025]",
                  ].join(" ")}
                />
              </div>

              <div
                className={[
                  "paranoidi-hero-stand print-products-image-card",
                  "group order-1 overflow-hidden rounded-[2rem]",
                  "border border-white/10 bg-zinc-950 p-3",
                  "shadow-2xl shadow-red-950/30",
                  "transition-colors duration-300",
                  "hover:border-red-400/30",
                  "sm:order-2 sm:p-4",
                  "theme-light:border-[#5a3824]/20",
                  "theme-light:bg-[#fff8ef]",
                  "theme-light:shadow-[0_20px_55px_rgba(84,23,23,0.14)]",
                ].join(" ")}
              >
                <img
                  src="/assets/print-products/paranoidi-matti-oksa-kangasstandi.webp"
                  alt={t("orderExtrasPage.hero.fabricStandAlt")}
                  className={[
                    "mx-auto h-auto w-full rounded-[1.4rem] object-contain",
                    "transition duration-500 group-hover:scale-[1.025]",
                  ].join(" ")}
                />
              </div>
            </div>

            <div
              className={[
                "relative z-10 mx-auto mt-4 flex w-fit items-center gap-3",
                "rounded-2xl border border-red-400/25 bg-black/85",
                "px-4 py-3 shadow-xl shadow-black/40 backdrop-blur",
                "theme-light:border-red-500/30",
                "theme-light:bg-[#fff8ef]",
                "theme-light:shadow-[0_12px_32px_rgba(84,23,23,0.14)]",
              ].join(" ")}
            >
              <span
                aria-hidden="true"
                className={[
                  "h-2.5 w-2.5 rounded-full bg-red-500",
                  "shadow-[0_0_18px_rgba(239,68,68,0.9)]",
                ].join(" ")}
              />

              <div>
                <p className="text-[0.65rem] font-black uppercase tracking-[0.16em] text-red-300 theme-light:text-[#b42318]">
                  {t("orderExtrasPage.hero.readyLabel")}
                </p>

                <p className="mt-1 text-sm font-black text-white theme-light:text-[#541717]">
                  {t("orderExtrasPage.hero.readyValue")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TUOTTEET */}
      <section
        id="tuotteet"
        className={[
          "scroll-mt-8 border-b border-white/10",
          "px-3 py-16 sm:px-4 md:py-24 lg:px-5",
          "theme-light:border-[#5a3824]/20",
        ].join(" ")}
      >
        <div className="mx-auto w-full max-w-7xl">
          <div className="print-products-fade-up max-w-4xl">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-red-300 theme-light:text-[#b42318]">
              {t("orderExtrasPage.products.eyebrow")}
            </p>

            <h2
              className={[
                "mt-4 text-3xl font-black tracking-[-0.04em] sm:text-5xl",
                "theme-light:text-[#241a16]",
              ].join(" ")}
            >
              {t("orderExtrasPage.products.title")}
            </h2>
          </div>

          {/* KANGASSTÄNDI */}
          <article
            className={[
              "print-products-panel print-products-fade-up",
              "print-products-delay-1 mt-12 overflow-hidden rounded-[2.5rem]",
              "border border-white/10",
              "bg-gradient-to-br from-zinc-950 to-black",
              "theme-light:border-[#5a3824]/20",
              "theme-light:from-[#fff8ef] theme-light:to-[#f2d8d5]",
              "theme-light:shadow-[0_24px_65px_rgba(58,43,24,0.12)]",
            ].join(" ")}
          >
            <div
              className={[
                "grid items-center gap-10 p-5 sm:p-8",
                "lg:grid-cols-[0.82fr_1.18fr] lg:p-10",
              ].join(" ")}
            >
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-red-300 theme-light:text-[#b42318]">
                  {t("orderExtrasPage.fabricStand.eyebrow")}
                </p>

                <h3
                  className={[
                    "mt-4 text-3xl font-black tracking-[-0.035em]",
                    "sm:text-4xl theme-light:text-[#241a16]",
                  ].join(" ")}
                >
                  {t("orderExtrasPage.fabricStand.title")}
                </h3>

                <p className="mt-5 text-base leading-8 text-zinc-400 theme-light:text-[#5a3824]">
                  {t("orderExtrasPage.fabricStand.description")}
                </p>

                <div className="mt-6 space-y-3 text-sm font-bold text-zinc-300 theme-light:text-[#541717]">
                  <p>✓ {t("orderExtrasPage.fabricStand.features.size")}</p>
                  <p>✓ {t("orderExtrasPage.fabricStand.features.portable")}</p>
                  <p>✓ {t("orderExtrasPage.fabricStand.features.branding")}</p>
                </div>

                <a
                  href="#editori"
                  className={[
                    "mt-8 inline-flex rounded-full bg-red-500",
                    "px-6 py-3 text-xs font-black uppercase",
                    "tracking-[0.1em] text-white",
                    "transition duration-300",
                    "hover:-translate-y-0.5 hover:bg-red-400",
                  ].join(" ")}
                >
                  {t("orderExtrasPage.fabricStand.edit")}
                </a>
              </div>

              <div
                className={[
                  "print-products-image-card group overflow-hidden",
                  "rounded-[2rem] border border-white/10",
                  "bg-zinc-950 p-4 sm:p-6",
                  "theme-light:border-[#5a3824]/20",
                  "theme-light:bg-[#fffdf9]",
                ].join(" ")}
              >
                <img
                  src="/assets/print-products/paranoidi-matti-oksa-kangasstandi.webp"
                  alt={t("orderExtrasPage.fabricStand.imageAlt")}
                  className={[
                    "mx-auto max-h-[43rem] w-full object-contain",
                    "transition duration-500 group-hover:scale-[1.02]",
                  ].join(" ")}
                />
              </div>
            </div>

          {/* PYSTYTYSVIDEO */}
          <div
            className={[
              "border-t border-white/10",
              "theme-light:border-[#5a3824]/20",
            ].join(" ")}
          >
            <div
              className={[
                "grid overflow-hidden",
                "bg-[linear-gradient(135deg,rgba(239,68,68,0.10),rgba(0,0,0,0.58))]",
                "lg:grid-cols-[0.72fr_1.28fr] lg:items-stretch",
                "theme-light:bg-[linear-gradient(135deg,#fff8ef,#f2d8d5)]",
              ].join(" ")}
            >
              <div className="relative flex flex-col justify-center p-6 sm:p-8 lg:p-10">
                <div
                  aria-hidden="true"
                  className={[
                    "pointer-events-none absolute -left-16 top-1/2",
                    "h-44 w-44 -translate-y-1/2 rounded-full",
                    "bg-red-500/10 blur-3xl",
                  ].join(" ")}
                />

                <div className="relative">
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-red-300 theme-light:text-[#b42318]">
                      {t("orderExtrasPage.video.eyebrow")}
                    </p>

                    <span
                      className={[
                        "rounded-full border border-red-400/25 bg-red-500/10",
                        "px-3 py-1 text-[0.62rem] font-black uppercase",
                        "tracking-[0.14em] text-red-200",
                        "theme-light:border-red-500/25",
                        "theme-light:bg-red-50 theme-light:text-[#b42318]",
                      ].join(" ")}
                    >
                      {t("orderExtrasPage.video.duration")}
                    </span>
                  </div>

                  <h4
                    className={[
                      "mt-4 max-w-lg text-3xl font-black",
                      "leading-[1.02] tracking-[-0.04em]",
                      "text-white sm:text-4xl",
                      "theme-light:text-[#241a16]",
                    ].join(" ")}
                  >
                    {t("orderExtrasPage.video.title")}
                  </h4>

                  <p
                    className={[
                      "mt-5 max-w-lg text-sm font-semibold leading-7",
                      "text-zinc-400 sm:text-base",
                      "theme-light:text-[#5a3824]",
                    ].join(" ")}
                  >
                    {t("orderExtrasPage.video.description")}
                  </p>

                  <div
                    className={[
                      "mt-6 flex flex-wrap gap-2",
                      "text-[0.65rem] font-black uppercase tracking-[0.1em]",
                      "text-zinc-300 theme-light:text-[#541717]",
                    ].join(" ")}
                  >
                    {videoTags.map((item) => (
                        <span
                          key={item}
                          className={[
                            "rounded-full border border-white/10 bg-white/[0.04]",
                            "px-3 py-2",
                            "theme-light:border-[#5a3824]/15",
                            "theme-light:bg-white/60",
                          ].join(" ")}
                        >
                          {item}
                        </span>
                      ))}
                  </div>
                </div>
              </div>

              <div
                className={[
                  "relative flex h-[18rem] items-center justify-center overflow-hidden bg-black",
                  "sm:h-[24rem] lg:h-[27rem]",
                ].join(" ")}
              >
                <video
                  controls
                  playsInline
                  preload="metadata"
                  className="h-full w-full bg-black object-contain object-center"
                >
                  <source
                    src="/assets/videos/kangasstandi-pystytys.mp4"
                    type="video/mp4"
                  />

                  {t("orderExtrasPage.video.unsupported")}
                </video>

                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/10"
                />
              </div>
            </div>
          </div>
          </article>

          {/* TUOPINALUSET */}
          <article
            className={[
              "print-products-panel print-products-fade-up",
              "print-products-delay-2 mt-8 rounded-[2.5rem]",
              "border border-white/10",
              "bg-gradient-to-br from-zinc-950 to-black",
              "p-5 sm:p-8 lg:p-10",
              "theme-light:border-[#5a3824]/20",
              "theme-light:from-[#fff8ef] theme-light:to-[#ead6c2]",
              "theme-light:shadow-[0_24px_65px_rgba(58,43,24,0.12)]",
            ].join(" ")}
          >
            <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_0.9fr]">
              <div
                className={[
                  "print-products-image-card group order-2 overflow-hidden",
                  "rounded-[2rem] border border-white/10",
                  "bg-zinc-950 p-4 sm:p-6 lg:order-1",
                  "theme-light:border-[#5a3824]/20",
                  "theme-light:bg-[#fffdf9]",
                ].join(" ")}
              >
                <img
                  src="/assets/print-products/paranoidi-matti-oksa-tuopinaluset.webp"
                  alt={t("orderExtrasPage.coasters.imageAlt")}
                  className={[
                    "mx-auto w-full object-contain",
                    "transition duration-500 group-hover:scale-[1.02]",
                  ].join(" ")}
                />
              </div>

              <div className="order-1 lg:order-2">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-red-300 theme-light:text-[#b42318]">
                  {t("orderExtrasPage.coasters.eyebrow")}
                </p>

                <h3
                  className={[
                    "mt-4 text-3xl font-black tracking-[-0.035em]",
                    "sm:text-4xl theme-light:text-[#241a16]",
                  ].join(" ")}
                >
                  {t("orderExtrasPage.coasters.title")}
                </h3>

                <p className="mt-5 text-base leading-8 text-zinc-400 theme-light:text-[#5a3824]">
                  {t("orderExtrasPage.coasters.description")}
                </p>

                <div className="mt-6 space-y-3 text-sm font-bold text-zinc-300 theme-light:text-[#541717]">
                  <p>✓ {t("orderExtrasPage.coasters.features.size")}</p>
                  <p>✓ {t("orderExtrasPage.coasters.features.sides")}</p>
                  <p>✓ {t("orderExtrasPage.coasters.features.quantities")}</p>
                </div>

                <a
                  href="#editori"
                  className={[
                    "mt-8 inline-flex rounded-full bg-red-500",
                    "px-6 py-3 text-xs font-black uppercase",
                    "tracking-[0.1em] text-white",
                    "transition duration-300",
                    "hover:-translate-y-0.5 hover:bg-red-400",
                  ].join(" ")}
                >
                  {t("orderExtrasPage.coasters.edit")}
                </a>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* EDITORI */}
      <section
        id="editori"
        className={[
          "scroll-mt-4 border-b border-white/10",
          "px-3 py-16 sm:px-4 md:py-24 lg:px-5",
          "theme-light:border-[#5a3824]/20",
        ].join(" ")}
      >
        <div className="mx-auto w-full max-w-7xl">
          <div className="print-products-fade-up mb-10 max-w-4xl">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-red-300 theme-light:text-[#b42318]">
              {t("orderExtrasPage.editor.eyebrow")}
            </p>

            <h2
              className={[
                "mt-4 text-3xl font-black tracking-[-0.04em] sm:text-5xl",
                "theme-light:text-[#241a16]",
              ].join(" ")}
            >
              {t("orderExtrasPage.editor.title")}
            </h2>

            <p className="mt-5 max-w-3xl text-base leading-8 text-zinc-400 theme-light:text-[#5a3824]">
              {t("orderExtrasPage.editor.description")}
            </p>
          </div>

            <div
              className={[
                "print-products-editor-shell rounded-[2.5rem]",
                "theme-light:shadow-[0_24px_65px_rgba(58,43,24,0.12)]",
              ].join(" ")}
            >
            <ProductCustomizer
              isLoggedIn={isLoggedIn}
              performer={performer}
            />
          </div>
        </div>
      </section>

      {/* HINTAAN SISÄLTYY */}
      <section className="px-3 py-16 sm:px-4 md:py-24 lg:px-5">
        <div
          className={[
            "print-products-included print-products-fade-up",
            "mx-auto w-full max-w-7xl rounded-[2.5rem]",
            "border border-red-400/25",
            "bg-gradient-to-br from-red-500/15 via-zinc-950 to-black",
            "p-6 sm:p-8 lg:p-10",
            "theme-light:border-red-500/30",
            "theme-light:from-[#f2d8d5]",
            "theme-light:via-[#fff8ef]",
            "theme-light:to-[#ead6c2]",
            "theme-light:shadow-[0_24px_65px_rgba(84,23,23,0.13)]",
          ].join(" ")}
        >
          <div className="max-w-3xl">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-red-300 theme-light:text-[#b42318]">
              {t("orderExtrasPage.included.eyebrow")}
            </p>

            <h2
              className={[
                "mt-4 text-3xl font-black tracking-[-0.035em]",
                "sm:text-4xl theme-light:text-[#241a16]",
              ].join(" ")}
            >
              {t("orderExtrasPage.included.title")}
            </h2>

            <p className="mt-4 text-base leading-7 text-zinc-400 theme-light:text-[#5a3824]">
              {t("orderExtrasPage.included.description")}
            </p>
          </div>

          <div className="mt-9 grid gap-4 md:grid-cols-2">
            {includedItems.map((item, index) => (
              <div
                key={item}
                className={[
                  "print-products-included-item flex items-center gap-4",
                  "rounded-2xl border border-white/10 bg-black/30",
                  "px-5 py-4 transition duration-300",
                  "hover:-translate-y-0.5 hover:border-red-400/25",
                  "theme-light:border-[#5a3824]/20",
                  "theme-light:bg-[#fffdf9]",
                  "theme-light:hover:border-red-500/30",
                ].join(" ")}
              >
                <span
                  className={[
                    "flex h-8 w-8 shrink-0 items-center justify-center",
                    "rounded-full bg-red-500 text-sm font-black text-white",
                    "shadow-[0_0_20px_rgba(239,68,68,0.22)]",
                  ].join(" ")}
                >
                  {String(index + 1).padStart(2, "0")}
                </span>

                <span className="text-sm font-bold leading-6 text-zinc-200 theme-light:text-[#541717]">
                  {item}
                </span>
              </div>
            ))}
          </div>

          <p
            className={[
              "mt-8 border-t border-white/10 pt-7",
              "text-sm leading-7 text-zinc-400",
              "theme-light:border-[#5a3824]/20",
              "theme-light:text-[#6b4229]",
            ].join(" ")}
          >
            {t("orderExtrasPage.included.note")}
          </p>
        </div>
      </section>
    </main>
  );
}